import {
  ConfigApi,
  createApiRef,
  FetchApi,
  OAuthApi,
} from '@backstage/core-plugin-api';

import { Application, Cluster, Tenant } from '../types';
import {
  deserializeApplication,
  deserializeCluster,
  deserializeTenant,
} from '../utils/jsonserializer';

export const kaapApiRef = createApiRef<KaapApi>({
  id: 'plugin.kaap',
});

// The auth backend's sign-in resolver reads the email from the id token on non-Graph tokens.
export const kaapApiScopes = (apiUrl: string): string[] => [
  'openid',
  'email',
  `${apiUrl}/.default`,
];

export interface KaapApi {
  getTenant(tenantName: string): Promise<Tenant | undefined>;
  getApplications(): Promise<Application[]>;
  getClusters(): Promise<Cluster[]>;
}

export type KaapApiClientOptions = {
  fetchApi: FetchApi;
  configApi: ConfigApi;
  microsoftAuth: OAuthApi;
};

export class KaapApiClient implements KaapApi {
  private readonly fetchApi: FetchApi;
  private readonly microsoftAuth: OAuthApi;
  private readonly apiUrl: string;
  private readonly baseUrl: string;

  constructor(options: KaapApiClientOptions) {
    this.fetchApi = options.fetchApi;
    this.microsoftAuth = options.microsoftAuth;
    this.apiUrl = options.configApi.getString('kaap.apiUrl');
    this.baseUrl = `${this.apiUrl}/v1`;
  }

  private getToken(): Promise<string> {
    return this.microsoftAuth.getAccessToken(kaapApiScopes(this.apiUrl));
  }

  async getTenant(tenantName: string): Promise<Tenant | undefined> {
    const token = await this.getToken();
    const url = `${this.baseUrl}/tenants/${tenantName}`;
    const response = await this.fetchApi.fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const tenant = await response.json();
    if (!tenant || tenant?.detail) {
      return undefined;
    }

    return deserializeTenant(tenant);
  }

  private applicationsCache?: { promise: Promise<Application[]>; at: number };
  private static readonly APPLICATIONS_TTL_MS = 5 * 60_000;

  // getApplications returns the list of applications, using a cache with TTL (5 minutes). This allows making multiple calls without hitting the API repeatedly.
  async getApplications(): Promise<Application[]> {
    const cached = this.applicationsCache;
    if (cached && Date.now() - cached.at < KaapApiClient.APPLICATIONS_TTL_MS) {
      return cached.promise;
    }

    const promise = this.fetchApplications().catch(error => {
      this.applicationsCache = undefined; // don't cache failures
      throw error;
    });
    this.applicationsCache = { promise, at: Date.now() };
    return promise;
  }

  private async fetchApplications(): Promise<Application[]> {
    const token = await this.getToken();
    const url = `${this.baseUrl}/applications`;
    const response = await this.fetchApi.fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const { applications } = await response.json();
    return (applications ?? []).map(deserializeApplication);
  }

  async getClusters(): Promise<Cluster[]> {
    const token = await this.getToken();
    const url = `${this.baseUrl}/clusters`;
    const response = await this.fetchApi.fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const { clusters } = await response.json();
    return (clusters ?? []).map(deserializeCluster);
  }
}
